
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from '@supabase/supabase-js';
import { setupServiceWorkerAuthListener, setupPWASessionRestoration } from '@/utils/pwa';
import { initActivityTracker } from '@/utils/activity-tracker';

import { logger } from '@/utils/logger';
import { PRODUCTION_CONFIG } from '@/config/production';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
};

// Create context with default values
const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  session: null,
  loading: true,
  isAuthenticated: false
});

// Check if running in PWA standalone mode
const isPWAStandalone = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://') ||
    localStorage.getItem('pwaStandalone') === 'true'
  );
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
    console.log("AuthProvider rendering - PWA mode:", isPWAStandalone());
  }
  
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
      console.log("AuthProvider - Setting up auth state listener");
    }
    
    let subscription: any = null;
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    let sessionCheckRetries = 0;
    const maxRetries = isPWAStandalone() ? 5 : 3;

    const updateAuthState = (newSession: Session | null) => {
      if (!isMounted) return;
      
      if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
        console.log("Updating auth state:", newSession?.user?.email || "No session");
      }
      
      // Synchronous auth state update to avoid deadlocks
      setUser(newSession?.user ?? null);
      setSession(newSession);
      setIsAuthenticated(!!newSession?.user);
      logger.setUserId(newSession?.user?.id);
      
      // Mark initialization complete and stop loading once
      if (!initializedRef.current) {
        setLoading(false);
        initializedRef.current = true;
      }
    };

    const checkExistingSession = async () => {
      try {
        if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
          console.log("AuthProvider - Checking for existing session (attempt", sessionCheckRetries + 1, ")");
        }
        const { data: sessionData, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error checking session:", error);
          if (sessionCheckRetries < maxRetries) {
            sessionCheckRetries++;
            setTimeout(checkExistingSession, 500); // Faster retry
            return;
          }
        }
        
        if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
          console.log("Session check result:", sessionData.session?.user?.email || "No session");
        }
        updateAuthState(sessionData.session);
      } catch (error) {
        console.error("Exception during session check:", error);
        if (sessionCheckRetries < maxRetries) {
          sessionCheckRetries++;
          setTimeout(checkExistingSession, 1000);
        } else {
          setLoading(false);
          setIsAuthenticated(false);
        }
      }
    };
    
    const setupAuth = async () => {
      try {
        // Set up the auth state change listener first
        const { data } = supabase.auth.onAuthStateChange((event, newSession) => {
          if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
            console.log("Auth state changed:", event, newSession?.user?.email);
          }
          
          if (isMounted) {
            updateAuthState(newSession);
            
            // Security: Clear sensitive data on logout
            if (event === 'SIGNED_OUT') {
              localStorage.removeItem('hasSeenLanding');
              sessionStorage.clear();
            }
          }
        });
        
        subscription = data.subscription;

        // Check for existing session with retry logic for PWA
        await checkExistingSession();
        
      } catch (error) {
        console.error("Error setting up auth:", error);
        if (isMounted) {
          setLoading(false);
          setIsAuthenticated(false);
        }
      }
    };
    
    // Fallback timeout to avoid indefinite loading, only if not initialized
    const timeoutDuration = 5000;
    timeoutId = setTimeout(() => {
      if (isMounted && !initializedRef.current) {
        console.warn("Auth initialization timeout, proceeding without auth");
        initializedRef.current = true;
        setLoading(false);
        setIsAuthenticated(false);
      }
    }, timeoutDuration);
    
    setupAuth();
    
    // Set up service worker auth listener for PWA direct navigation
    try {
      setupServiceWorkerAuthListener();
      setupPWASessionRestoration();
    } catch (error) {
      console.error("Error setting up service worker auth listener:", error);
    }

    return () => {
      if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
        console.log("Cleaning up auth subscription");
      }
      isMounted = false;
      clearTimeout(timeoutId);
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Initialize activity tracker when user is authenticated
  useEffect(() => {
    if (!loading && user && isAuthenticated) {
      try {
        initActivityTracker();
      } catch (error) {
        console.error("Error initializing activity tracker:", error);
      }
    }
  }, [loading, user, isAuthenticated]);

  const value = {
    user,
    session,
    loading,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
